import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Joyride from 'react-joyride';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import LivePriceTicker from '../components/LivePriceTicker/LivePriceTicker';
import TradeSummary from '../components/TradeSummary/TradeSummary';
import WhaleTradesTable from '../components/WhaleTradesTable/WhaleTradesTable';
import LiquidationsTable from '../components/LiquidationsTable/LiquidationsTable';
import NewsfeedPanel from '../components/NewsfeedPanel/NewsfeedPanel';
import BigVsSmall from '../components/BigVsSmall/BigVsSmall';
import BtcPredictions from '../components/BtcPredictions/BtcPredictions';
import UnusualActivity from '../components/UnusualActivity/UnusualActivity';
import Stats24h from '../components/Stats24h/Stats24h';
import TradingViewChart from '../components/TradingViewChart/TradingViewChart';
import DebugPanel from '../components/DebugPanel/DebugPanel';
import UnusualActivityBanner from '../components/UnusualActivityBanner/UnusualActivityBanner';
import WelcomeDialog from '../components/WelcomeDialog/WelcomeDialog';
import InterfaceChooser from '../components/InterfaceChooser/InterfaceChooser';
import { useWebSocket } from '../hooks/useWebSocket';
import { useFilters } from '../hooks/useFilters';
import { useLivePrices } from '../hooks/useLivePrices';
import { useTopTrades } from '../hooks/useTopTrades';
import { useOnboarding } from '../hooks/useOnboarding';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useInterfacePreference } from '../hooks/useInterfacePreference';
import { useLanguage } from '../contexts/LanguageContext';
import { getCoinIconUrl } from '../utils/helpers';
import { API_BASE_URL } from '../utils/apiConfig';
import { getTradesTourSteps } from '../config/tradesTourSteps';
import './TradingPage.css';
import '../styles/joyride-overrides.css';

const DEFAULT_TICKER_COINS = ['BTC', 'ETH', 'SOL', 'BNB', 'LINK', 'DOGE', 'XRP', 'EOS', 'LTC', 'HYPE']
  .map(s => ({ symbol: s, icon: getCoinIconUrl(s) }));

function TradingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { localizedPath } = useLanguage();
  const { getPreference, hasChosen } = useInterfacePreference();
  const [showChooser, setShowChooser] = useState(false);

  // Auto-redirect pro users, show chooser for first-time visitors
  useEffect(() => {
    const pref = getPreference();
    if (pref === 'pro') {
      navigate(localizedPath('/terminal'), { replace: true });
    } else if (!hasChosen()) {
      setShowChooser(true);
    }
  }, []);

  // Onboarding tour
  const {
    tourRunning,
    stepIndex,
    showWelcome,
    startTour,
    dismissWelcome,
    restartTour,
    handleJoyrideCallback
  } = useOnboarding('trades', { totalSteps: 8 });
  const tourSteps = useMemo(() => getTradesTourSteps(t), [t]);

  // Feature flags
  const showPredictions = useFeatureFlag('btc-predictions', true);

  // Combined dashboard stats (replaces 3 separate polling hooks)
  const dashboardStats = useDashboardStats({ hours: 24, topCoinsLimit: 10 });

  // Ticker coins from admin config
  const [tickerCoins, setTickerCoins] = useState(DEFAULT_TICKER_COINS);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/ticker-coins`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.coins?.length > 0) {
          setTickerCoins(data.coins.map(s => ({ symbol: s, icon: getCoinIconUrl(s) })));
        }
      })
      .catch(() => {});
  }, []);

  // Chart state
  const [chartSymbol, setChartSymbol] = useState(null);

  // Shared tooltip state - when one table opens a tooltip, close the other
  const closeWhaleTooltipRef = useRef(null);
  const closeLiqTooltipRef = useRef(null);

  // Check if test mode is enabled via query parameter
  const [isTestMode, setIsTestMode] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const testMode = params.get('isTestMode') === 'true';
    setIsTestMode(testMode);
  }, []);

  // Use custom hooks
  const { filters, setWhaleTradesMinVolume, setLiquidationsMinVolume, setSelectedCoins, toggleCategory, clearCategories, shouldDisplayTrade, getSummary, isFilterStrictFromStorage, resetToDefaults } = useFilters();

  // Strict filter banner state
  const [showStrictBanner, setShowStrictBanner] = useState(true);

  // Connect to WebSocket - it will send historical data immediately after subscription
  const {
    isConnected,
    isSubscribed,
    subscriptionError,
    whaleTrades: liveWhaleTrades,
    liquidations: liveLiquidations,
    historicalTrades,
    historicalLoading: loading,
    summary,
    metrics,
    subscribe,
    unsubscribe,
    simulateTrade,
    simulateLiquidation,
    // Unusual activity methods
    unusualActivity,
    subscribeToUnusualActivity,
    unsubscribeFromUnusualActivity,
    changeUnusualActivityTimeframe
  } = useWebSocket(filters, {
    enabled: true
  });

  const { prices, formatPrice } = useLivePrices();

  // Fetch top 3 whale trades from entire database (once on page load)
  const { topTrades: top3WhaleTradesFromAPI } = useTopTrades('whale_trade', 3);

  // Update document title with BTC price
  useEffect(() => {
    if (prices.BTC) {
      const btcPrice = Math.round(prices.BTC).toLocaleString('en-US');
      document.title = `BTC $${btcPrice} - TraderMap`;
    }
  }, [prices.BTC]);

  // Restore title only on unmount
  useEffect(() => {
    return () => { document.title = 'TraderMap'; };
  }, []);

  // Split historical trades by type
  const historicalWhaleTrades = useMemo(() => {
    return historicalTrades.filter(t => !t.isLiquidation);
  }, [historicalTrades]);

  const historicalLiquidations = useMemo(() => {
    return historicalTrades.filter(t => t.isLiquidation);
  }, [historicalTrades]);

  // Combine historical trades from WebSocket with live WebSocket data
  const allWhaleTrades = useMemo(() => {
    // Create a Map to deduplicate by unique key (exchange + symbol + timestamp + price)
    const tradesMap = new Map();

    // Add historical trades from WebSocket (already filtered by server)
    historicalWhaleTrades.forEach(trade => {
      const key = `${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.price}`;
      tradesMap.set(key, trade);
    });

    // Add live WebSocket trades (already filtered by server)
    liveWhaleTrades.forEach(trade => {
      const key = `${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.price}`;
      tradesMap.set(key, trade);
    });

    // Convert to array, filter by category, sort by timestamp (newest first), then limit to 300
    let combined = Array.from(tradesMap.values());

    // Apply category filter client-side
    combined = combined.filter(trade => shouldDisplayTrade(trade));

    combined.sort((a, b) => {
      const aTime = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
      const bTime = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
      return bTime - aTime; // Newest first
    });

    return { trades: combined.slice(0, 300) };
  }, [historicalWhaleTrades, liveWhaleTrades, shouldDisplayTrade]);

  // Enrich top 3 whale trades from API with baseCoin (logoUrl already loaded by useTopTrades hook)
  const top3WhaleTrades = useMemo(() => {
    return top3WhaleTradesFromAPI.map(trade => {
      const baseCoin = trade.symbol?.replace(/USDT$|USD$/i, '').toUpperCase() || '';
      return { ...trade, baseCoin };
    });
  }, [top3WhaleTradesFromAPI]);

  // Combine historical liquidations from DB with live WebSocket data
  const allLiquidations = useMemo(() => {
    // Create a Map to deduplicate by unique key
    const liquidationsMap = new Map();

    // Add historical liquidations from WebSocket (already filtered by server)
    historicalLiquidations.forEach(liq => {
      const key = `${liq.exchange}-${liq.symbol}-${liq.timestamp}-${liq.price}`;
      liquidationsMap.set(key, liq);
    });

    // Add live WebSocket liquidations (already filtered by server)
    liveLiquidations.forEach(liq => {
      const key = `${liq.exchange}-${liq.symbol}-${liq.timestamp}-${liq.price}`;
      liquidationsMap.set(key, liq);
    });

    // Convert to array, filter by category, sort by timestamp (newest first), then limit to 300
    let combined = Array.from(liquidationsMap.values());

    // Apply category filter client-side
    combined = combined.filter(trade => shouldDisplayTrade({ ...trade, isLiquidation: true }));

    combined.sort((a, b) => {
      const aTime = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
      const bTime = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
      return bTime - aTime; // Newest first
    });

    return combined.slice(0, 300);
  }, [historicalLiquidations, liveLiquidations, shouldDisplayTrade]);

  // Handle Live Chart open
  const handleOpenChart = (symbol) => {
    setChartSymbol(symbol);
  };

  // Handle chart close
  const handleCloseChart = () => {
    setChartSymbol(null);
  };

  return (
    <div className="trading-page">
      <SEO page="trades" skipTitle />
      <Header
        isConnected={isConnected}
        isSubscribed={isSubscribed}
        subscriptionError={subscriptionError}
        onStartTour={restartTour}
      />
      <h1 className="sr-only">Real-Time Whale Trades & Liquidations | TraderMap</h1>

      {/* Unusual Activity Alert Banner */}
      <UnusualActivityBanner />

      {/* Strict Filter Warning Banner */}
      {isFilterStrictFromStorage && showStrictBanner && (
        <div className="container-fluid">
          <div className="strict-filter-banner">
            <span>{t('filters.strictFilterWarning', 'Your saved filters may be too strict — you might be missing trades.')}</span>
            <div className="strict-filter-banner-actions">
              <button className="strict-filter-banner-reset" onClick={() => { resetToDefaults(); setShowStrictBanner(false); }}>
                {t('filters.resetFilters', 'Reset Filters')}
              </button>
              <button className="strict-filter-banner-dismiss" onClick={() => setShowStrictBanner(false)}>
                &times;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Panel - shown when ?isTestMode=true */}
      {isTestMode && (
        <DebugPanel
          onSimulateTrade={simulateTrade}
          onSimulateLiquidation={simulateLiquidation}
          onSubscribe={subscribe}
          onUnsubscribe={unsubscribe}
          isConnected={isConnected}
          isSubscribed={isSubscribed}
        />
      )}

      {/* TradingView Chart - shown when chartSymbol is set */}
      {chartSymbol && (
        <TradingViewChart
          symbol={chartSymbol}
          onClose={handleCloseChart}
        />
      )}

      <LivePriceTicker
        prices={prices}
        formatPrice={formatPrice}
        filters={filters}
        setWhaleTradesMinVolume={setWhaleTradesMinVolume}
        setLiquidationsMinVolume={setLiquidationsMinVolume}
        setSelectedCoins={setSelectedCoins}
        toggleCategory={toggleCategory}
        clearCategories={clearCategories}
        tickerCoins={tickerCoins}
      />

      <div className="container-fluid">
        <div className="grid">
          <div className="g-col-12 g-col-lg-8 trading-left-wrap">
            <div className="trading-tables-row">
              <div className="trading-table-col" data-tour="whale-trades-card">
                <WhaleTradesTable
                  trades={allWhaleTrades.trades}
                  top3Trades={top3WhaleTrades}
                  loading={loading}
                  prices={prices}
                  formatPrice={formatPrice}
                  onOpenChart={handleOpenChart}
                  summary={summary}
                  selectedCoins={filters.selectedCoins}
                  filters={filters}
                  registerCloseTooltip={(fn) => { closeWhaleTooltipRef.current = fn; }}
                  onOtherTableClick={() => closeLiqTooltipRef.current?.()}
                  externalTopCoins={dashboardStats.topCoinsByCount}
                />
              </div>
              <div className="trading-table-col" data-tour="liquidations-card">
                <LiquidationsTable
                  liquidations={allLiquidations}
                  loading={loading}
                  summary={summary}
                  prices={prices}
                  formatPrice={formatPrice}
                  onOpenChart={handleOpenChart}
                  selectedCoins={filters.selectedCoins}
                  filters={filters}
                  registerCloseTooltip={(fn) => { closeLiqTooltipRef.current = fn; }}
                  onOtherTableClick={() => closeWhaleTooltipRef.current?.()}
                />
              </div>
            </div>
            <div className="trading-bottom-row">
              {showPredictions && (
                <div className="trading-bottom-left">
                  <BtcPredictions
                    btcPrice={prices?.BTC ?? null}
                    predictionsData={dashboardStats.btcPredictions}
                    predictionsTimeframe={dashboardStats.btcTimeframe}
                    setPredictionsTimeframe={dashboardStats.setBtcTimeframe}
                    predictionsLoading={dashboardStats.loading}
                  />
                </div>
              )}
              <div className="trading-bottom-right">
                <Stats24h summary={summary} />
              </div>
            </div>
          </div>

          <div className="g-col-12 g-col-lg-4" data-tour="right-column">
            <NewsfeedPanel />
            <BigVsSmall
              variant="compact"
              distributionData={dashboardStats.tradeSizeDistribution}
              distributionTotalTrades={dashboardStats.totalTrades}
              distributionLoading={dashboardStats.loading}
            />
            <UnusualActivity
              unusualActivity={unusualActivity}
              subscribeToUnusualActivity={subscribeToUnusualActivity}
              unsubscribeFromUnusualActivity={unsubscribeFromUnusualActivity}
              changeUnusualActivityTimeframe={changeUnusualActivityTimeframe}
              isConnected={isConnected}
            />
          </div>
        </div>
      </div>

      {/* Interface Chooser - shown before onboarding for first-time visitors */}
      {showChooser && <InterfaceChooser onDismiss={() => setShowChooser(false)} />}

      {/* Onboarding Tour */}
      <WelcomeDialog
        show={showWelcome}
        onStartTour={startTour}
        onDismiss={dismissWelcome}
      />
      <Joyride
        steps={tourSteps}
        run={tourRunning}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous
        showSkipButton
        showProgress
        disableScrolling
        disableOverlayClose
        spotlightPadding={8}
        locale={{
          back: t('onboarding.back', 'Back'),
          close: t('onboarding.close', 'Close'),
          last: t('onboarding.finish', 'Got it!'),
          next: t('onboarding.next', 'Next'),
          skip: t('onboarding.skip', 'Skip tour')
        }}
        styles={{
          options: {
            zIndex: 10000,
            arrowColor: '#1a1a1a',
            backgroundColor: '#1a1a1a',
            overlayColor: 'rgba(0, 0, 0, 0.6)',
            primaryColor: '#fff',
            textColor: '#fff'
          }
        }}
      />
    </div>
  );
}

export default TradingPage;
